<script setup lang="ts">
import { computed, watch, type PropType } from 'vue'
import DialogRevamp from '@/components/DialogRevamp.vue'
import BaseButton from '@/components/ui/button/BaseButton.vue'
import useAuthDialog from '@/lib/useAuthDialog'
import injectStrict from '@/lib/inject'
import BaseIcon from '@/components/ui/icon/BaseIcon.vue'
import { isMobile } from '@/legacy/ts/checkDevice'
import { createGAEvent, GaGeneralEvent } from '@/assets/ts/customEvent'
import { getMcrRootUrl } from '@/lib/siteBaseUrl'
import { getCurrentGaPageName } from '@/lib/utils'
import { isGlobalSite } from '@/lib/checkService'

enum TypeSuccess {
	LoginVerify = 'loginVerify',
	ResetPassword = 'resetPassword',
	SignupNormal = 'signupNormal',
	SignupBuyforyou = 'signupBuyforyou',
	SignupEc = 'signupEc',
	SignupMercariBrand = 'signupMercariBrand',
}

const { isShowDialog } = useAuthDialog(hideDialogDirectly)
const textContent: any = injectStrict('textContent')
const isInGlobalSite = isGlobalSite()

const props = defineProps({
	type: {
		type: String as PropType<`${TypeSuccess}`>,
		default: TypeSuccess.SignupNormal,
	},
})

const emit = defineEmits(['hide'])

const isTypeLoginVerify = computed(() => props.type === TypeSuccess.LoginVerify)
const isTypeResetPassword = computed(
	() => props.type === TypeSuccess.ResetPassword,
)
const isTypeSignupNormal = computed(
	() => props.type === TypeSuccess.SignupNormal,
)
const isTypeSignupBuyforyou = computed(
	() => props.type === TypeSuccess.SignupBuyforyou,
)
const isTypeSignupEc = computed(() => props.type === TypeSuccess.SignupEc)

const isTypeSignupMercariBrand = computed(
	() => props.type === TypeSuccess.SignupMercariBrand,
)
const currentImg = computed(() =>
	isTypeLoginVerify.value ? 'login' : 'signup',
)
const currentTitle = computed(() => {
	switch (props.type) {
		case TypeSuccess.LoginVerify:
			return textContent.login_success_unverified_title
		case TypeSuccess.ResetPassword:
			return textContent.reset_password_successful_title
		default:
			return textContent.registered_success_title
	}
})

const currentSubtitle = computed(() => {
	switch (props.type) {
		case TypeSuccess.LoginVerify:
			return textContent.login_success_unverified_subtitle
		case TypeSuccess.ResetPassword:
			return textContent.reset_password_successful_subtitle
		case TypeSuccess.SignupNormal:
			return textContent.registered_success_subtitle_normal
		case TypeSuccess.SignupBuyforyou:
			return textContent.registered_success_subtitle_buyforyou
		case TypeSuccess.SignupEc:
			return textContent.registered_success_subtitle_express_checkout
		default:
			return textContent.registered_success_subtitle_normal
	}
})

const subTitleWithCredits = computed(() => {
	const welcomeCredit = window.BUYANDSHIP.settings.member_welcome_credits
	return currentSubtitle.value.replace('(welcome_credit)', welcomeCredit)
})

const currentPrimaryButtonText = computed(() => {
	switch (props.type) {
		case TypeSuccess.LoginVerify:
			return textContent.login_success_unverified_button_to_mcr
		case TypeSuccess.ResetPassword:
			return textContent.reset_password_successful_button
		case TypeSuccess.SignupNormal:
			return textContent.registered_success_button_verify_number
		case TypeSuccess.SignupBuyforyou:
			return textContent.registered_success_button_continue_order
		case TypeSuccess.SignupEc:
			return textContent.registered_success_button_continue_shopping
		case TypeSuccess.SignupMercariBrand:
			return textContent.registered_success_button_keep_current_page
		default:
			return textContent.login_success_unverified_button_to_mcr
	}
})

const currentSecondaryButtonText = computed(() => {
	switch (props.type) {
		case TypeSuccess.LoginVerify:
			return textContent.login_success_unverified_button_stay
		case TypeSuccess.SignupNormal:
			return textContent.registered_success_button_stay
		case TypeSuccess.SignupBuyforyou:
			return isInGlobalSite
				? textContent.registered_success_button_visit_member_centre
				: textContent.registered_success_button_verify_number
		case TypeSuccess.SignupEc:
			return isInGlobalSite
				? textContent.registered_success_button_visit_member_centre
				: textContent.registered_success_button_verify_number
		case TypeSuccess.SignupMercariBrand:
			return textContent.registered_success_button_go_verify
		default:
			return ''
	}
})

const postId = computed(() => {
	if (!import.meta.env.PROD) {
		const url = new URL(window.location.href)
		return (
			(isTypeSignupEc.value
				? url.searchParams.get('postId')
				: url.searchParams.get('id')) || ''
		)
	}
	const regextEn = /^\/en/
	const regexPost = /\/sharing\/posts\//g
	const finalPath = window.location.pathname
		.replace(regextEn, '')
		.replace(regexPost, '')
	return isTypeSignupEc.value
		? finalPath.replace('/checkout/', '')
		: finalPath.replace('/', '')
})

const currentPageSource = computed(() => {
	const url = new URL(window.location.href)
	return url.searchParams.get('fromPostId')
		? 'Product Sharing Post Detail Bottom Recommendation'
		: 'Product Sharing Post Listing'
})

function hideDialogDirectly() {
	emit('hide')
}

function toMcrVerify() {
	createGAEvent(GaGeneralEvent.ButtonClick, {
		button_name: 'Pop-up Verify Account',
		...(getCurrentGaPageName() && { page_name: getCurrentGaPageName() }),
	})
	window.location.href = `${getMcrRootUrl()}/profile/settings/user-profile/`
}

function handleClickPrimaryButton() {
	if (isTypeLoginVerify.value || isTypeSignupNormal.value) {
		toMcrVerify()
	}
	if (isTypeSignupBuyforyou.value || isTypeSignupEc.value) {
		createGAEvent(GaGeneralEvent.ButtonClick, {
			page_name: isTypeSignupBuyforyou.value
				? 'Community Post Detail Page'
				: 'Checkout Page',
			page_object_id: `post_${postId.value}`,
			post_id: postId.value,
			is_express_checkout: isTypeSignupEc.value,
			page_source: currentPageSource.value,
			button_name: isTypeSignupEc.value
				? 'Proceed to Payment'
				: 'Add To New Request',
		})

		triggerActionByDOM()
	}
	// Mercari Brand primary button should hide dialog directly
	hideDialogDirectly()
}

function handleClickSecondaryButton() {
	if (
		isTypeSignupBuyforyou.value ||
		isTypeSignupEc.value ||
		isTypeSignupMercariBrand.value
	) {
		if (isInGlobalSite) {
			window.location.href = `${getMcrRootUrl()}/account/`
			return
		}

		toMcrVerify()
	}

	hideDialogDirectly()
}

function triggerActionByDOM() {
	// no easy way to trigger product sharing action by vue, dirty work around
	const triggerButtonClick = (selector: string) => {
		const button = document.querySelector<HTMLElement>(selector)
		if (button) {
			button.click()
			return true
		}

		return false
	}

	const tryMultipleSelectors = (selectors: string[]) => {
		return selectors.some(selector => triggerButtonClick(selector))
	}

	const handleButtonAfterLoading = () => {
		window.BsHeaderVm.isNeedTriggerActionButton = true
	}

	const signupButtonSelector = '#checkout-page-submit-button .bs-button-normal'
	const sharingButtonSelector =
		'#success-dialog-primary-button .bs-button-normal'

	const baseButtonSelector = '#success-dialog-primary-button'

	if (isTypeSignupEc.value) {
		if (!triggerButtonClick(signupButtonSelector)) {
			handleButtonAfterLoading()
		}
	} else if (
		// This check is needed because BsButtonNormal and BaseButton have different structures.
		// If the first selector doesn't find a button, try the second one.
		// It should return false so that handleButtonAfterLoading() is triggered when the background has not fully loaded.
		!tryMultipleSelectors([sharingButtonSelector, baseButtonSelector])
	) {
		handleButtonAfterLoading()
	}
}

watch(
	() => isShowDialog.value,
	value => {
		if (!value) {
			hideDialogDirectly()
		}
	},
)
</script>

<template>
	<DialogRevamp
		class="bs-auth-success"
		v-model="isShowDialog"
		:isFullInMobile="true"
		:isShowDefaultClose="isMobile"
		:size="'sm'"
	>
		<template #header v-if="isMobile">
			<!-- only for special strange layout -->
			<div v-if="isMobile"></div>
		</template>
		<template #content>
			<button
				class="bs-auth-success__custom-close bs-icon"
				@click="hideDialogDirectly"
			>
				<BaseIcon :name="'close_v1'" class="h-24px w-24px fill-n-950" />
			</button>
			<div class="bs-auth-success__content">
				<div
					class="bs-auth-success__content__img"
					:class="`bs-auth-success__content__img--${currentImg}`"
				></div>

				<h4 class="bs-auth-success__content__title">{{ currentTitle }}</h4>
				<div
					class="bs-auth-success__content__subtitle"
					v-html="subTitleWithCredits"
				></div>
			</div>
		</template>
		<template v-slot:footer>
			<div class="bs-auth-success__footer">
				<BaseButton
					v-if="!isTypeResetPassword"
					class="bs-auth-success__footer__button"
					:isFull="true"
					:variant="'secondary'"
					:size="'large'"
					@click="handleClickSecondaryButton"
				>
					{{ currentSecondaryButtonText }}
				</BaseButton>
				<BaseButton
					class="bs-auth-success__footer__button"
					:isFull="true"
					:variant="'primary'"
					:size="'large'"
					@click="handleClickPrimaryButton"
				>
					{{ currentPrimaryButtonText }}
				</BaseButton>
			</div>
		</template>
	</DialogRevamp>
</template>
